/**
 * Admin · Pending Students
 *
 * Lists all submitted public applications waiting for admin approval.
 * Each row exposes the applicant profile + uploaded documents, plus an
 * "Approve student" action that opens the existing StudentEnrollmentWizard
 * pre-filled with the application data. On wizard success we mark the
 * application as approved so it drops off this list.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    GraduationCap, FileSearch, Download, FileText, Mail, Loader2,
    UserCheck, Search, ChevronRight,
} from "lucide-react";
import StudentEnrollmentWizard from "@/components/admin/StudentEnrollmentWizard";

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }); }
    catch { return iso; }
}

function fmtError(e, fb) { return e?.response?.data?.detail || e?.message || fb; }

/**
 * Map an application record onto the StudentEnrollmentWizard's BLANK form
 * shape so the admin can review/edit before final enrolment.
 */
function appToWizardPreset(app, programs) {
    const fullName = [app.first_name, app.middle_name, app.last_name]
        .filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    // Match the program from the application to a wizard-known program so the
    // program select is auto-populated. If the program is missing/archived,
    // we leave it blank so the admin must pick one.
    const matchedProgram = (programs || []).find((p) => p.id === app.program_id);
    return {
        email: app.email || "",
        name: fullName,
        first_name: app.first_name || "",
        last_name: app.last_name || "",
        gender: app.gender || "",
        sin: app.sin || "",
        visa_status: app.visa_status || "",
        campus: app.campus || "",
        admission_term: app.admission_term || "",
        student_language: "English",
        phone_local: app.phone || "",
        country: app.country || "",
        student_id: app.student_id || "",
        program_id: matchedProgram?.id || "",
        residency: (app.country || "").toLowerCase() === "canada" ? "domestic" : "international",
        send_welcome_email: true,
        dob: app.date_of_birth || "",
        address_line1: app.address_line1 || "",
        address_city: app.city || "",
        address_province: app.state_province || "",
        address_postal_code: app.postal_code || "",
        address_country: app.country || "Canada",
        generate_loa: true,
        email_loa: true,
        credential_type: app.program_category === "diploma" ? "diploma" : "certificate",
    };
}

export default function PendingStudents() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [reviewing, setReviewing] = useState(null);
    const [approving, setApproving] = useState(null); // application being approved
    const [programs, setPrograms] = useState([]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/applications", {
                params: { status: "submitted", ...(q ? { q } : {}) },
            });
            setItems(data.items || []);
        } catch (e) {
            toast.error(fmtError(e, "Could not load pending applications"));
        } finally {
            setLoading(false);
        }
    }, [q]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        // Programs list — needed so the wizard can match application.program_id
        // and so we can render a friendly program title in the table.
        let cancelled = false;
        api.get("/programs", { params: { include_archived: true } })
            .then(({ data }) => { if (!cancelled) setPrograms(data || []); })
            .catch(() => { /* non-fatal */ });
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(() => {
        if (!q.trim()) return items;
        const needle = q.trim().toLowerCase();
        return items.filter((a) =>
            (a.first_name + " " + a.last_name).toLowerCase().includes(needle)
            || a.email?.toLowerCase().includes(needle)
            || a.student_id?.toLowerCase().includes(needle)
            || a.program_id?.toLowerCase().includes(needle),
        );
    }, [items, q]);

    const programTitle = useCallback((pid) => {
        const p = programs.find((x) => x.id === pid);
        return p?.title || pid;
    }, [programs]);

    const onWizardDone = async (result) => {
        if (!approving) return;
        try {
            await api.post(`/admin/applications/${approving.id}/approve`, {
                user_id: result?.user?.id,
                enrollment_id: result?.enrollment_id,
            });
            toast.success("Student approved & moved to Enrolled list.");
            // drop from local list optimistically
            setItems((prev) => prev.filter((a) => a.id !== approving.id));
            setApproving(null);
        } catch (e) {
            toast.error(fmtError(e, "Approval was recorded but the application status update failed. Please refresh."));
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-10 pb-24" data-testid="admin-pending-students">
            <Link to="/admin" className="text-sm text-muted-foreground hover:text-primary">
                ← Back
            </Link>
            <div className="mt-2 mb-8">
                <div className="inline-flex items-center gap-1.5 chip chip-primary">
                    <GraduationCap className="h-3 w-3" />
                    Admissions
                </div>
                <h1 className="font-display text-3xl md:text-4xl text-foreground mt-3 tracking-tight">
                    Pending students
                </h1>
                <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
                    Applicants who submitted the public Apply Now form. Review their
                    documents, then approve to create their student account, issue an
                    LOA, and send the welcome email.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <Card className="p-3 bg-[#FBF5E8] border-[#E1DAC5]">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
                        Awaiting approval
                    </div>
                    <div className="font-display text-3xl mt-1 text-[#134B30]">{items.length}</div>
                </Card>
                <Card className="p-3 bg-emerald-50 border-emerald-200">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
                        Documents archived
                    </div>
                    <div className="font-display text-3xl mt-1 text-[#134B30]">
                        {items.reduce((acc, a) => acc + (a.documents?.length || 0), 0)}
                    </div>
                </Card>
                <Card className="p-3 bg-amber-50 border-amber-200">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
                        Diplomas / Certificates
                    </div>
                    <div className="font-display text-base mt-1 text-[#134B30]">
                        <span className="mr-3">
                            {items.filter((a) => a.program_category === "diploma").length} Diploma
                        </span>
                        <span>
                            {items.filter((a) => a.program_category === "certificate").length} Certificate
                        </span>
                    </div>
                </Card>
            </div>

            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="relative w-full sm:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, email, HCCD ID or program…"
                        className="pl-9"
                        data-testid="pending-search"
                    />
                </div>
                <p className="text-xs text-muted-foreground">
                    Showing <strong>{filtered.length}</strong> of {items.length}
                </p>
            </div>

            <Card className="surface-card border-border">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-12 flex items-center justify-center text-muted-foreground">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-16 text-center text-sm text-muted-foreground">
                            <FileSearch className="h-10 w-10 mx-auto text-[#C79520]/60 mb-3" />
                            No applications awaiting approval right now.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="pending-table">
                                <thead className="bg-muted/40 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-semibold">Student ID</th>
                                        <th className="text-left px-4 py-3 font-semibold">Applicant</th>
                                        <th className="text-left px-4 py-3 font-semibold">Program / Term</th>
                                        <th className="text-left px-4 py-3 font-semibold">Campus</th>
                                        <th className="text-center px-4 py-3 font-semibold">Docs</th>
                                        <th className="text-left px-4 py-3 font-semibold">Submitted</th>
                                        <th className="text-right px-4 py-3 font-semibold">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((a) => (
                                        <tr key={a.id} className="border-t border-border hover:bg-muted/20 transition" data-testid={`pending-row-${a.id}`}>
                                            <td className="px-4 py-3 font-mono text-xs">{a.student_id}</td>
                                            <td className="px-4 py-3">
                                                <div className="font-medium">{a.first_name} {a.last_name}</div>
                                                <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                                                    <Mail className="h-3 w-3" />{a.email}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-xs">
                                                <div className="font-medium">{programTitle(a.program_id)}</div>
                                                <div className="text-muted-foreground capitalize">{a.program_category} · {a.admission_term}</div>
                                            </td>
                                            <td className="px-4 py-3 text-xs">{a.campus}</td>
                                            <td className="px-4 py-3 text-center text-xs">
                                                <Badge variant="outline" className="text-[10px]">
                                                    {a.documents?.length || 0}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-xs">{fmtDate(a.submitted_at || a.created_at)}</td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="inline-flex gap-1.5">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setReviewing(a)}
                                                        data-testid={`pending-view-${a.id}`}
                                                    >
                                                        <FileSearch className="h-3 w-3 mr-1" /> View
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => setApproving(a)}
                                                        className="bg-[#134B30] hover:bg-[#0E3A24] text-white gap-1"
                                                        data-testid={`pending-approve-${a.id}`}
                                                    >
                                                        <UserCheck className="h-3 w-3" />
                                                        Approve student
                                                        <ChevronRight className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* View applicant detail + documents */}
            <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
                <DialogContent className="max-w-3xl" data-testid="pending-detail-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">
                            {reviewing?.first_name} {reviewing?.last_name} — {reviewing?.student_id}
                        </DialogTitle>
                    </DialogHeader>
                    {reviewing && (
                        <div className="space-y-4 text-sm">
                            <div className="bg-[#FBF5E8] border border-[#E1DAC5] rounded-md p-3 grid grid-cols-2 gap-2 text-xs">
                                <div><strong>Email:</strong> {reviewing.email}</div>
                                <div><strong>Phone:</strong> {reviewing.phone || "—"}</div>
                                <div><strong>DOB:</strong> {fmtDate(reviewing.date_of_birth)}</div>
                                <div><strong>Gender:</strong> {reviewing.gender || "—"}</div>
                                <div><strong>Visa status:</strong> {reviewing.visa_status || "—"}</div>
                                <div><strong>Country:</strong> {reviewing.country}</div>
                                {reviewing.country?.toLowerCase() === "canada" && (
                                    <div><strong>SIN:</strong> {reviewing.sin || "—"}</div>
                                )}
                                <div><strong>Program:</strong> {programTitle(reviewing.program_id)}</div>
                                <div className="capitalize"><strong>Category:</strong> {reviewing.program_category}</div>
                                <div><strong>Term:</strong> {reviewing.admission_term}</div>
                                <div><strong>Campus:</strong> {reviewing.campus}</div>
                                <div className="col-span-2"><strong>Address:</strong> {[reviewing.address_line1, reviewing.address_line2, reviewing.city, reviewing.state_province, reviewing.postal_code].filter(Boolean).join(", ") || "—"}</div>
                            </div>
                            <div>
                                <div className="text-sm font-semibold mb-2">
                                    Uploaded documents ({reviewing.documents?.length || 0})
                                </div>
                                {(reviewing.documents || []).length === 0 ? (
                                    <div className="text-xs text-muted-foreground italic">No documents uploaded.</div>
                                ) : (
                                    <ul className="space-y-1">
                                        {reviewing.documents.map((d) => (
                                            <li key={d.id} className="flex items-center justify-between text-xs bg-muted/20 rounded px-2 py-1.5">
                                                <span className="flex items-center gap-1.5 truncate">
                                                    <FileText className="h-3 w-3 text-[#134B30] flex-shrink-0" />
                                                    <Badge variant="outline" className="text-[10px]">{d.category_label}</Badge>
                                                    <span className="truncate">{d.filename}</span>
                                                    <span className="text-muted-foreground">({Math.round(d.size / 1024)} KB)</span>
                                                </span>
                                                <a
                                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/applications/${reviewing.id}/documents/${d.id}/download`}
                                                    download={d.filename}
                                                    className="text-[#134B30] hover:underline inline-flex items-center gap-1"
                                                    data-testid={`pending-download-doc-${d.id}`}
                                                >
                                                    <Download className="h-3 w-3" />
                                                    Download
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setReviewing(null)} data-testid="pending-detail-close">
                            Close
                        </Button>
                        {reviewing && (
                            <Button
                                onClick={() => { setApproving(reviewing); setReviewing(null); }}
                                className="bg-[#134B30] hover:bg-[#0E3A24] text-white gap-1"
                                data-testid="pending-detail-approve"
                            >
                                <UserCheck className="h-4 w-4" />
                                Approve student
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Approval wizard — reuses StudentEnrollmentWizard pre-filled */}
            <StudentEnrollmentWizard
                open={!!approving}
                onOpenChange={(o) => !o && setApproving(null)}
                programs={programs.filter((p) => !p.is_deleted && !p.is_archived && p.is_published)}
                presetEmail={approving?.email || ""}
                presetData={approving ? appToWizardPreset(approving, programs) : null}
                onDone={onWizardDone}
            />
        </div>
    );
}
